import type { GeneratedQuestion } from '../generators/math/types';
import type { ReviewPaper } from '../review/types';
import type { PersonalReviewPaper } from '../review/personal-review';
import type { ListeningExam } from '../review/listening-exam-types';
import type { MathExam } from '../review/math-exam-gen';
import type { StatsTableExam, StatsTableData } from '../review/stats-table-gen';
import { formatHM } from '../review/stats-table-gen';
import type { TimeExam } from '../review/time-exam-gen';
import type { MulDivExam } from '../review/mul-div-gen';
import type { CircleExam } from '../review/circle-gen';
import type { DecimalExam, DecimalQuestion } from '../review/decimal-gen';

export interface QuizPack {
  packId: string;
  subject: string;
  kpId: string;
  questions: GeneratedQuestion[];
  generatedAtISO: string;
}

/**
 * Generate a print-ready HTML document as a string.
 * Caller opens this in a new window and calls window.print() — the browser
 * uses the OS system Chinese font, producing a real PDF via the OS print
 * dialog with proper Chinese rendering. Avoids jsPDF's CJK font weight.
 */
export function generatePrintableHtml(pack: QuizPack): string {
  const questionsHtml = pack.questions.map((q, i) => {
    const num = i + 1;
    if (q.kind === 'mcq') {
      const opts = q.options.map((o, idx) =>
        `<span class="opt">(${String.fromCharCode(65 + idx)}) ${escapeHtml(String(o))}</span>`
      ).join('');
      return `
        <div class="q">
          <div class="prompt">${num}. ${escapeHtml(q.prompt)}</div>
          <div class="opts">${opts}</div>
          <div class="answer-line">答:<span class="blank">______</span></div>
        </div>
      `;
    } else {
      return `
        <div class="q">
          <div class="prompt">${num}. ${escapeHtml(q.prompt)}</div>
          <div class="fill-box"></div>
        </div>
      `;
    }
  }).join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>永恆要塞 練習卷 — ${escapeHtml(pack.subject)} ${escapeHtml(pack.kpId)}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .q { margin-bottom: 18px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 8px; }
  .opts { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; margin-bottom: 8px; }
  .opt { padding: 2px 8px; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { display: inline-block; min-width: 3em; border-bottom: 1px solid #000; padding: 0 4px; }
  .fill-box { border: 1px solid #000; width: 80mm; height: 20mm; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">永恆要塞 練習卷 — ${escapeHtml(pack.subject)} ${escapeHtml(pack.kpId)}</div>
  <div class="meta">
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(pack.packId)}
  </div>
</div>
${questionsHtml}
</body>
</html>`;
}

/**
 * Generate a print-ready double-sided A4 HTML document for a ReviewPaper.
 *
 * Spec: docs/spec/LLM_WIKI_REVIEW_SPEC.md §6. Output is exactly two page
 * sections — printer "duplex" = one physical sheet, two faces:
 *
 *   • Front (page 1): header (姓名/日期/卷號/計時欄/分數欄) + `front` questions
 *     (MCQ options / fill boxes), reusing the existing A4 portrait CSS + CJK
 *     font stack.
 *   • Back (page 2): `page-break-before: always`. Top half = English dictation
 *     blanks (numbered 1..N, NO English word printed — the app plays audio).
 *     Bottom half = 訂正/計算區 + 卷號 QR placeholder (paperId text + TODO; no
 *     frontend QR generator exists yet — see TODO below).
 *
 * Caller opens the returned HTML in a new window and calls window.print().
 */
export function generateDoubleSidedHtml(paper: ReviewPaper): string {
  const frontHtml = paper.front.map((q, i) => {
    const num = i + 1;
    if (q.kind === 'mcq') {
      const opts = (q.options ?? []).map((o, idx) =>
        `<span class="opt">(${String.fromCharCode(65 + idx)}) ${escapeHtml(String(o))}</span>`
      ).join('');
      return `
        <div class="q">
          <div class="prompt">${num}. ${escapeHtml(q.prompt)}</div>
          <div class="opts">${opts}</div>
          <div class="answer-line">答:<span class="blank">______</span></div>
        </div>
      `;
    }
    return `
        <div class="q">
          <div class="prompt">${num}. ${escapeHtml(q.prompt)}</div>
          <div class="fill-box"></div>
        </div>
      `;
  }).join('');

  // Dictation: print ONLY the item number + a blank. The English `word` is
  // NEVER emitted (prevents leaking the answer; the app plays it via TTS).
  const dictation = paper.dictation ?? [];
  const dictationHtml = dictation.map((d, i) => {
    const num = i + 1;
    const hint = d.hintZh ? ` <span class="dict-hint">(${escapeHtml(d.hintZh)})</span>` : '';
    return `
        <div class="dict-item">
          <span class="dict-num">${num}.</span>
          <span class="dict-blank"></span>${hint}
        </div>
      `;
  }).join('');

  const title = `永恆要塞 複習卷 — ${escapeHtml(paper.paperId)}`;
  const nameValue = paper.studentName
    ? escapeHtml(paper.studentName)
    : '&nbsp;';

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .page { page-break-inside: auto; }
  .page-back { page-break-before: always; }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .q { margin-bottom: 18px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 8px; }
  .opts { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; margin-bottom: 8px; }
  .opt { padding: 2px 8px; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { display: inline-block; min-width: 3em; border-bottom: 1px solid #000; padding: 0 4px; }
  .fill-box { border: 1px solid #000; width: 80mm; height: 20mm; }
  .section-title { font-size: 16px; font-weight: bold; margin: 12px 0 10px; border-left: 4px solid #000; padding-left: 8px; }
  .dict-item { font-size: 14px; margin-bottom: 14px; display: flex; align-items: baseline; gap: 8px; }
  .dict-num { min-width: 2em; }
  .dict-blank { display: inline-block; flex: 1; max-width: 60mm; border-bottom: 1px solid #000; height: 1.2em; }
  .dict-hint { font-size: 12px; color: #333; }
  .correction { border: 1px solid #000; width: 100%; height: 70mm; margin-top: 10px; }
  .qr-area { display: flex; justify-content: flex-end; margin-top: 10px; }
  .qr-box { border: 1px dashed #000; padding: 6px 10px; font-size: 11px; text-align: center; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="page page-front">
  <div class="header">
    <div class="title">${title}</div>
    <div class="meta">
      姓名:<span class="blank">${nameValue}</span>
      日期:<span class="blank">&nbsp;</span>
      卷號:${escapeHtml(paper.paperId)}
    </div>
    <div class="scorebar">
      <div class="box">作答時間:____ 分</div>
      <div class="box">分數:____ / ${paper.front.length}</div>
    </div>
  </div>
  ${frontHtml}
</div>
<div class="page page-back">
  <div class="section-title">英文聽寫(聽 app 播音,在空格寫出單字)</div>
  ${dictationHtml}
  <div class="section-title">訂正 / 計算區</div>
  <div class="correction"></div>
  <div class="qr-area">
    <div class="qr-box">
      卷號 ${escapeHtml(paper.paperId)}
      <!-- TODO(QR): no frontend QR generator exists yet (worker qrcode-generator
           is worker-only, async PNG). Render paperId as text placeholder; swap
           in a QR <img>/<svg> once a client-side generator is available. -->
    </div>
  </div>
</div>
</body>
</html>`;
}

/**
 * 個人化複習卷 → 列印用 HTML(C task §3「組成一張像考卷的複習卷」)。
 *
 * 與 `generateDoubleSidedHtml`(LLM-Wiki 雙面卷)不同,這張卷:
 *   • **按科分大題**(一、二、三…),每科一個 section,題目用整卷連續題號。
 *   • 每題 = 題號 + 題幹 + 手寫作答格(fill 樣式)。原錯題不另外標記(避免暗示)。
 *   • 卷尾附「解答」區,預設用 `<details>` 收合,列印 / 點開才看到 —— 對應 UI 的
 *     「顯示解答」需求,讓家長批改方便,孩子作答時看不到。
 *   • 沿用既有 A4 portrait CSS + CJK font stack;純黑白、無彩光(教學專業)。
 *
 * Caller 開新視窗 write 這段 HTML 後 `window.print()`。
 */
export function generatePersonalReviewHtml(paper: PersonalReviewPaper): string {
  const sectionLabels = ['一', '二', '三', '四', '五', '六', '七', '八'];

  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = paper.sections.map((section, sIdx) => {
    const sLabel = sectionLabels[sIdx] ?? String(sIdx + 1);
    const qsHtml = section.items.map((item) => {
      runningIdx += 1;
      const num = runningIdx;
      answerRows.push(
        `<li class="ans-row"><span class="ans-num">${num}.</span> ${escapeHtml(item.displayAnswer || '—')}</li>`,
      );
      return `
        <div class="q">
          <div class="prompt">${num}. ${escapeHtml(item.prompt)}</div>
          <div class="fill-box"></div>
        </div>
      `;
    }).join('');
    return `
      <div class="rv-section">
        <div class="section-title">${sLabel}、${escapeHtml(section.label)}</div>
        ${qsHtml}
      </div>
    `;
  }).join('');

  const total = runningIdx;
  const title = `永恆要塞 個人化複習卷 — ${escapeHtml(paper.paperId)}`;
  const headline = `個人化複習卷 · 針對 ${paper.weakKpCount} 個弱點`;
  const nameValue = paper.studentName ? escapeHtml(paper.studentName) : '&nbsp;';

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 14px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .rv-section { margin-bottom: 14px; }
  .section-title { font-size: 16px; font-weight: bold; margin: 16px 0 10px; border-left: 4px solid #000; padding-left: 8px; }
  .q { margin-bottom: 18px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 8px; }
  .fill-box { border: 1px solid #000; width: 80mm; height: 18mm; }
  .answers { page-break-before: always; }
  .answers .section-title { border-left-color: #000; }
  .ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .ans-row { margin-bottom: 6px; break-inside: avoid; }
  .ans-num { font-weight: bold; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } details.answers { display: block; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">${escapeHtml(headline)}</div>
  <div class="meta">
    姓名:<span class="blank">${nameValue}</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(paper.paperId)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<details class="answers">
  <summary class="section-title" style="cursor:pointer;">解答(批改用,點開 / 列印才顯示)</summary>
  <ul class="ans-list">${answerRows.join('')}</ul>
</details>
</body>
</html>`;
}

/**
 * 英語聽力卷 → 列印用 A4 HTML(spec §5)。
 *
 * 排版比照原卷:標題 + 四 Part(Part 標籤 + 指示語 + 題目),學生用筆寫
 * (Part A 勾 / Part B 選 / Part C ○× / Part D 圈)。**答案另頁** answer key 供批改。
 * 音檔:老師 / 家長用線上頁播音(spoken 不印,避免洩答)。純黑白、無彩光(教學專業)。
 *
 * Caller 開新視窗 write 這段 HTML 後 `window.print()`。
 */
export function generateListeningExamHtml(exam: ListeningExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  // 累計答案(answer key)列;每題一個顯示用正解 label。
  const answerRows: string[] = [];

  const partsHtml = exam.parts
    .map((part) => {
      const itemsHtml = part.items
        .map((_item, i) => {
          const num = i + 1;
          if (part.kind === 'listen-check') {
            const it = part.items[i];
            const opts = it.options
              .map((o) => `<span class="lp-opt">☐ ${escapeHtml(o)}</span>`)
              .join('');
            answerRows.push(`${part.label}-${num}. ${escapeHtml(it.options[it.answerIndex])}`);
            return `<div class="lp-q"><span class="lp-num">${num}.</span>${opts}</div>`;
          }
          if (part.kind === 'listen-choose') {
            const it = part.items[i];
            const opts = it.choices
              .map((c, ci) => `<span class="lp-opt">(${ci + 1}) ${escapeHtml(c)}</span>`)
              .join('');
            answerRows.push(`${part.label}-${num}. (${it.answerIndex + 1})`);
            return `<div class="lp-q"><span class="lp-num">${num}.</span>${opts}</div>`;
          }
          if (part.kind === 'listen-mark') {
            const it = part.items[i];
            answerRows.push(`${part.label}-${num}. ${it.answer === 'O' ? '○' : '×'}`);
            return `<div class="lp-q"><span class="lp-num">${num}.</span><span class="lp-printed">${escapeHtml(it.printed)}</span><span class="lp-mark">(　　)</span></div>`;
          }
          // listen-circle
          const it = part.items[i];
          const before = it.before ? `${escapeHtml(it.before)} ` : '';
          const after = it.after ? ` ${escapeHtml(it.after)}` : '';
          const opts = `( ${escapeHtml(it.options[0])} / ${escapeHtml(it.options[1])} )`;
          answerRows.push(`${part.label}-${num}. ${escapeHtml(it.options[it.answerIndex])}`);
          return `<div class="lp-q"><span class="lp-num">${num}.</span>${before}${opts}${after}</div>`;
        })
        .join('');
      return `
        <div class="lp-part">
          <div class="lp-part-head">${escapeHtml(part.label)} — ${escapeHtml(part.title)}<span class="lp-part-pts">(每題 ${part.pointsEach} 分)</span></div>
          <div class="lp-part-instr">${escapeHtml(part.instructions)}</div>
          ${itemsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="lp-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .lp-note { font-size: 12px; color: #333; margin: 8px 0 14px; }
  .lp-part { margin-bottom: 16px; page-break-inside: avoid; }
  .lp-part-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 12px 0 6px; }
  .lp-part-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .lp-part-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .lp-q { font-size: 14px; margin-bottom: 10px; display: flex; flex-wrap: wrap; align-items: baseline; gap: 10px; }
  .lp-num { min-width: 1.8em; font-weight: bold; }
  .lp-opt { padding: 0 6px; }
  .lp-printed { }
  .lp-mark { border-bottom: 1px solid #000; min-width: 3em; text-align: center; }
  .answers { page-break-before: always; }
  .lp-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .lp-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="meta">
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / 61</div>
  </div>
</div>
<div class="lp-note">聽力說明:由老師 / 家長用線上播放頁放音(每題播一段),學生依聽到的內容作答。</div>
${partsHtml}
<div class="answers">
  <div class="lp-part-head">解答(批改用 · 另頁)</div>
  <ul class="lp-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/**
 * 數學考卷 → 列印用 A4 HTML(數學「出考卷產生器」交付物之一)。
 *
 * 排版比照真卷分大題(一、二、三、四),每題 = 題號 + 題幹 + 手寫作答格
 * (直式除法留驗算大框、其餘留作答線)。**答案另頁** answer key 供批改。
 * 純黑白、無彩光(教學專業)。Caller 開新視窗 write 這段 HTML 後 `window.print()`。
 */
export function generateMathExamHtml(exam: MathExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  // 累計總分 + answer key 列。
  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const itemsHtml = section.items
        .map((item) => {
          runningIdx += 1;
          total += section.pointsEach;
          const num = runningIdx;
          answerRows.push(`${num}. ${escapeHtml(item.answer)}`);
          if (item.kind === 'compute' || item.kind === 'mulvert') {
            // 直式除法 / 乘法直式:題幹 + 大計算框(留直式 / 驗算空間)。
            return `<div class="mp-q"><div class="mp-prompt">${num}. ${escapeHtml(item.prompt)}</div><div class="mp-calc-box"></div></div>`;
          }
          if (item.kind === 'arrow' || item.kind === 'inverse') {
            // 算式填空 / 互逆:題幹自帶空格,下方留作答線。
            return `<div class="mp-q"><div class="mp-prompt">${num}. ${escapeHtml(item.prompt)}</div><div class="mp-line"></div></div>`;
          }
          // 應用題(word / remword / twostep):題幹 + 算式線 + 答案線。
          return `<div class="mp-q"><div class="mp-prompt">${num}. ${escapeHtml(item.prompt)}</div><div class="mp-line">算式:</div><div class="mp-line">答:</div></div>`;
        })
        .join('');
      return `
        <div class="mp-section">
          <div class="mp-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="mp-section-pts">(每題 ${section.pointsEach} 分)</span></div>
          <div class="mp-section-instr">${escapeHtml(section.instructions)}</div>
          ${itemsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="mp-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .mp-section { margin-bottom: 16px; }
  .mp-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .mp-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .mp-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .mp-q { margin-bottom: 16px; page-break-inside: avoid; }
  .mp-prompt { font-size: 14px; margin-bottom: 8px; }
  .mp-calc-box { border: 1px solid #000; width: 100%; height: 38mm; }
  .mp-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; margin-bottom: 8px; padding: 2px 0; }
  .answers { page-break-before: always; }
  .mp-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .mp-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="mp-section-head">解答(批改用 · 另頁)</div>
  <ul class="mp-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/**
 * 統計表考卷 → 列印用 A4 HTML(統計表「表格判讀」考卷交付物之一)。
 *
 * 每大題先**畫出該張表**(票價表 / 價目表 / 二維功課表,純黑白格線),再列題 +
 * 作答線。**答案另頁** answer key 供批改。表格 `border-collapse` + `page-break-inside`
 * 避免破版。Caller 開新視窗 write 這段 HTML 後 `window.print()`。
 */
export function generateStatsTableExamHtml(exam: StatsTableExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const tableHtml = renderStatsTableHtml(section.table);
      const qsHtml = section.questions
        .map((q) => {
          runningIdx += 1;
          total += section.pointsEach;
          const num = runningIdx;
          answerRows.push(`${num}. ${escapeHtml(q.answer)}`);
          return `<div class="st-q"><div class="st-prompt">${num}. ${escapeHtml(q.prompt)}</div><div class="st-line">答:</div></div>`;
        })
        .join('');
      return `
        <div class="st-section">
          <div class="st-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="st-section-pts">(每題 ${section.pointsEach} 分)</span></div>
          <div class="st-section-instr">${escapeHtml(section.instructions)}</div>
          ${tableHtml}
          ${qsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="st-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .st-section { margin-bottom: 16px; page-break-inside: avoid; }
  .st-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .st-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .st-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .st-table-caption { font-weight: bold; font-size: 14px; margin: 4px 0 6px; }
  table.st-table { border-collapse: collapse; font-size: 13px; margin-bottom: 12px; page-break-inside: avoid; }
  table.st-table th, table.st-table td { border: 1px solid #000; padding: 5px 10px; text-align: center; }
  table.st-table th { background: #eee; font-weight: bold; }
  .st-q { margin-bottom: 14px; page-break-inside: avoid; }
  .st-prompt { font-size: 14px; margin-bottom: 6px; }
  .st-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; padding: 2px 0; }
  .answers { page-break-before: always; }
  .st-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .st-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="st-section-head">解答(批改用 · 另頁)</div>
  <ul class="st-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/** 把一張統計表畫成 HTML <table>(三型分流,純黑白格線)。 */
function renderStatsTableHtml(table: StatsTableData): string {
  const cap = `<div class="st-table-caption">${escapeHtml(table.caption)}</div>`;
  if (table.kind === 'price') {
    const head = table.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
    const rows = table.rows
      .map(
        (r) =>
          `<tr><td>${escapeHtml(r.name)}</td><td>${escapeHtml(r.display ?? `${r.value} ${table.unit}`)}</td></tr>`,
      )
      .join('');
    return `${cap}<table class="st-table"><tr>${head}</tr>${rows}</table>`;
  }
  if (table.kind === 'menu') {
    const head = [table.groupAName, '價錢', table.groupBName, '價錢']
      .map((h) => `<th>${escapeHtml(h)}</th>`)
      .join('');
    const n = Math.max(table.groupA.length, table.groupB.length);
    const rows: string[] = [];
    for (let i = 0; i < n; i++) {
      const a = table.groupA[i];
      const b = table.groupB[i];
      const cells = [
        a ? escapeHtml(a.name) : '',
        a ? `${a.value} ${escapeHtml(table.unit)}` : '',
        b ? escapeHtml(b.name) : '',
        b ? `${b.value} ${escapeHtml(table.unit)}` : '',
      ];
      rows.push(`<tr>${cells.map((c) => `<td>${c}</td>`).join('')}</tr>`);
    }
    return `${cap}<table class="st-table"><tr>${head}</tr>${rows.join('')}</table>`;
  }
  if (table.kind === 'schedule') {
    const head =
      `<th></th>` + table.days.map((d) => `<th>${escapeHtml(d)}</th>`).join('');
    const rows = table.periods
      .map((per, p) => {
        const cells = table.grid[p].map((c) => `<td>${escapeHtml(c)}</td>`).join('');
        return `<tr><th>${escapeHtml(per)}</th>${cells}</tr>`;
      })
      .join('');
    return `${cap}<table class="st-table"><tr>${head}</tr>${rows}</table>`;
  }
  if (table.kind === 'quantity') {
    const head = table.categories.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
    const vals = table.counts
      .map((v) => `<td>${v} ${escapeHtml(table.unit)}</td>`)
      .join('');
    return `${cap}<table class="st-table"><tr>${head}</tr><tr>${vals}</tr></table>`;
  }
  if (table.kind === 'transit') {
    const head =
      `<th>車次</th>` + table.stations.map((s) => `<th>${escapeHtml(s)}</th>`).join('');
    const rows = table.trains
      .map((tr, r) => {
        const cells = table.grid[r]
          .map((c) => `<td>${c === null ? '—' : escapeHtml(formatHM(c))}</td>`)
          .join('');
        return `<tr><th>${escapeHtml(tr)}</th>${cells}</tr>`;
      })
      .join('');
    return `${cap}<table class="st-table"><tr>${head}</tr>${rows}</table>`;
  }
  if (table.kind === 'program-schedule') {
    const head = `<th>時間</th><th>節目</th>`;
    const rows = table.programs
      .map(
        (p, r) =>
          `<tr><td>${escapeHtml(formatHM(table.times[r]))}</td><td>${escapeHtml(p)}</td></tr>`,
      )
      .join('');
    return `${cap}<table class="st-table"><tr>${head}</tr>${rows}</table>`;
  }
  // fareMatrix
  const head =
    `<th>起站＼到站</th>` + table.stations.map((s) => `<th>${escapeHtml(s)}</th>`).join('');
  const rows = table.stations
    .map((s, i) => {
      const cells = table.stations
        .map((_, j) => `<td>${i === j ? '—' : `${table.matrix[i][j]} ${escapeHtml(table.unit)}`}</td>`)
        .join('');
      return `<tr><th>${escapeHtml(s)}</th>${cells}</tr>`;
    })
    .join('');
  return `${cap}<table class="st-table"><tr>${head}</tr>${rows}</table>`;
}

/**
 * 時間考卷 A4 列印 HTML(康軒三下「時間」單元:時段 / 12↔24 換算 / AM-PM / 時分秒加減)。
 * 分大題(一二三四)+ 每題作答線 + 解答另頁(answer key)。純黑白、A4 直式、避免破版。
 */
export function generateTimeExamHtml(exam: TimeExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const qsHtml = section.questions
        .map((q) => {
          runningIdx += 1;
          total += section.pointsEach;
          const num = runningIdx;
          answerRows.push(`${num}. ${escapeHtml(q.answer)}`);
          return `<div class="tm-q"><div class="tm-prompt">${num}. ${escapeHtml(q.prompt)}</div><div class="tm-line">答:</div></div>`;
        })
        .join('');
      return `
        <div class="tm-section">
          <div class="tm-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="tm-section-pts">(每題 ${section.pointsEach} 分)</span></div>
          <div class="tm-section-instr">${escapeHtml(section.instructions)}</div>
          ${qsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="tm-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .tm-section { margin-bottom: 16px; page-break-inside: avoid; }
  .tm-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .tm-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .tm-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .tm-q { margin-bottom: 14px; page-break-inside: avoid; }
  .tm-prompt { font-size: 14px; margin-bottom: 6px; }
  .tm-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; padding: 2px 0; }
  .answers { page-break-before: always; }
  .tm-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .tm-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="tm-section-head">解答(批改用 · 另頁)</div>
  <ul class="tm-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/**
 * 乘除考卷 A4 列印 HTML(康軒三下「第 7 單元 乘法與除法」:乘除互逆 / 未知數 / 倍數 /
 * 直式除法含驗算 / 應用題)。比照真段考排版:班級姓名得分 header + 大題標籤 + 指示語 +
 * 配分 + 作答格(直式除法給直式計算框 + 驗算欄、互逆題給多行算式格、其餘給作答線)+
 * 解答另頁。純黑白、A4 直式、表格 / 直式不破版(page-break-inside:avoid)。
 */
export function generateMulDivExamHtml(exam: MulDivExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const qsHtml = section.questions
        .map((q) => {
          runningIdx += 1;
          total += section.pointsEach;
          const num = runningIdx;
          // 解答另頁:多行算式把換行換成「、」單行顯示。
          answerRows.push(`${num}. ${escapeHtml(q.answer.replace(/\n+/g, '、'))}`);
          // 作答區依大題型給不同格子(直式框 / 多行算式 / 作答線)。
          let answerArea: string;
          if (section.kind === 'longdiv') {
            answerArea =
              `<div class="md-work">` +
              `<div class="md-work-box">直式計算</div>` +
              `<div class="md-work-box">驗算</div>` +
              `</div>` +
              `<div class="md-line">答:</div>`;
          } else if (section.kind === 'inverse') {
            answerArea =
              `<div class="md-lines">` +
              `<div class="md-line">　</div>` +
              `<div class="md-line">　</div>` +
              `<div class="md-line">　</div>` +
              `</div>`;
          } else {
            answerArea = `<div class="md-line">答:</div>`;
          }
          return `<div class="md-q"><div class="md-prompt">${num}. ${escapeHtml(q.prompt)}</div>${answerArea}</div>`;
        })
        .join('');
      return `
        <div class="md-section">
          <div class="md-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="md-section-pts">(每題 ${section.pointsEach} 分)</span></div>
          <div class="md-section-instr">${escapeHtml(section.instructions)}</div>
          ${qsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="md-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .md-section { margin-bottom: 16px; }
  .md-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .md-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .md-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .md-q { margin-bottom: 14px; page-break-inside: avoid; }
  .md-prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .md-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; padding: 2px 0; }
  .md-lines { display: flex; flex-direction: column; gap: 8px; }
  .md-lines .md-line { border-bottom: 1px solid #000; }
  .md-work { display: flex; gap: 12px; margin-bottom: 8px; }
  .md-work-box { flex: 1; border: 1px solid #000; min-height: 70px; padding: 4px 6px; font-size: 11px; color: #666; }
  .answers { page-break-before: always; }
  .md-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .md-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    班級:<span class="blank">&nbsp;</span>
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="md-section-head">解答(批改用 · 另頁)</div>
  <ul class="md-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/**
 * 圓考卷 A4 列印 HTML(康軒三下「第 6 單元 圓」:半徑↔直徑換算 / 名詞認識 / 判斷對錯 /
 * 用圓規畫圓 / 圓規操作)。比照真段考排版:班級姓名得分 header + 大題標籤 + 指示語 + 配分 +
 * 作答格(換算 / 圓規概念給作答線、名詞 / 判斷給選項 + 圈選格、圓規操作給作圖方格 + 圓規符號)+
 * 解答另頁。純黑白、A4 直式、作圖框不破版(page-break-inside:avoid)。
 *
 * 圓規操作(construct,printOnly)大題在卷面以「方格作圖區」呈現,線上不批改、解答給作圖提示。
 */
export function generateCircleExamHtml(exam: CircleExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const qsHtml = section.questions
        .map((q) => {
          runningIdx += 1;
          // printOnly(圓規操作)大題不計入線上總分(pointsEach=0),仍列印題目與作圖區。
          total += section.pointsEach;
          const num = runningIdx;
          answerRows.push(`${num}. ${escapeHtml(q.answer)}`);
          // 作答區依大題型給不同格子。
          let answerArea: string;
          if (section.kind === 'construct') {
            // 圓規作圖:方格作圖區 + 圓規符號提示。
            answerArea =
              `<div class="cir-draw">` +
              `<div class="cir-draw-hint">用圓規作圖（針腳固定圓心）</div>` +
              `<div class="cir-grid"></div>` +
              `</div>`;
          } else if (section.kind === 'term' || section.kind === 'judge') {
            // 名詞 / 判斷:列出選項供圈選。
            const choices = (q.choices ?? []).map((c) => `（　）${escapeHtml(c)}`).join('　　');
            answerArea = `<div class="cir-choices-print">${choices}　答:<span class="cir-blank"></span></div>`;
          } else {
            answerArea = `<div class="cir-line">答:</div>`;
          }
          return `<div class="cir-q"><div class="cir-prompt">${num}. ${escapeHtml(q.prompt)}</div>${answerArea}</div>`;
        })
        .join('');
      const ptsLabel = section.pointsEach > 0 ? `(每題 ${section.pointsEach} 分)` : '(實作題,不計線上分)';
      return `
        <div class="cir-section">
          <div class="cir-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="cir-section-pts">${ptsLabel}</span></div>
          <div class="cir-section-instr">${escapeHtml(section.instructions)}</div>
          ${qsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="cir-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .cir-section { margin-bottom: 16px; }
  .cir-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .cir-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .cir-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .cir-q { margin-bottom: 14px; page-break-inside: avoid; }
  .cir-prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .cir-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; padding: 2px 0; }
  .cir-choices-print { font-size: 13px; }
  .cir-blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .cir-draw { margin-bottom: 8px; }
  .cir-draw-hint { font-size: 11px; color: #666; margin-bottom: 4px; }
  .cir-grid {
    border: 1px solid #000; height: 150px;
    background-image:
      linear-gradient(to right, #ddd 1px, transparent 1px),
      linear-gradient(to bottom, #ddd 1px, transparent 1px);
    background-size: 10px 10px;
  }
  .answers { page-break-before: always; }
  .cir-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .cir-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    班級:<span class="blank">&nbsp;</span>
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="cir-section-head">解答(批改用 · 另頁)</div>
  <ul class="cir-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/**
 * 第 5 單元「小數」A4 段考排版(`generateDecimalExam` 的卷 → 印表 HTML)。
 * 對照 generateCircleExamHtml:大題標頭 + 配分 + 指示 + 逐題作答區;答案另頁。
 *
 * 各題型作答區:
 *   - compare(比大小):列出 `(　) >　(　) <　(　) =` 供圈選 + 一格答。
 *   - figure 塗色題:畫 10 格方格(塗 N 格上色,grounded 課本塗色)+ 答格。
 *   - figure 數線題:畫 SVG 數線(0..maxInt、每大格 10 小格、箭頭指點)+ 答格。
 *   - 其餘(互換 / 加減 / 應用):答題橫線。
 *
 * A4 硬規則:塗色格 / 數線寬度鎖死(≤ 內容寬),`page-break-inside:avoid` 防題目跨頁切半,
 * 不水平溢出。
 */
export function generateDecimalExamHtml(exam: DecimalExam): string {
  const title = `永恆要塞 ${escapeHtml(exam.title)}`;

  let total = 0;
  let runningIdx = 0;
  const answerRows: string[] = [];

  const sectionsHtml = exam.sections
    .map((section) => {
      const qsHtml = section.questions
        .map((q) => {
          runningIdx += 1;
          total += section.pointsEach;
          const num = runningIdx;
          // 存原始答案(含 >/</= 等),交給 answerKeyHtml 的 escapeHtml 單次轉義;
          // 別在此處先 escape,否則 answerKeyHtml 再 escape 一次 = 雙重轉義(>→&amp;gt;)。
          answerRows.push(`${num}. ${q.answer}`);
          const figureHtml = q.figure ? decimalFigurePrintHtml(q.figure) : '';
          let answerArea: string;
          if (section.kind === 'compare') {
            answerArea =
              `<div class="dec-compare-print">在()裡填:` +
              `（　）&gt;　（　）&lt;　（　）=　　答:<span class="dec-blank"></span></div>`;
          } else {
            answerArea = `<div class="dec-line">答:</div>`;
          }
          return `<div class="dec-q"><div class="dec-prompt">${num}. ${escapeHtml(q.prompt)}</div>${figureHtml}${answerArea}</div>`;
        })
        .join('');
      const ptsLabel = `(每題 ${section.pointsEach} 分)`;
      return `
        <div class="dec-section">
          <div class="dec-section-head">${escapeHtml(section.label)}、${escapeHtml(section.title)}<span class="dec-section-pts">${ptsLabel}</span></div>
          <div class="dec-section-instr">${escapeHtml(section.instructions)}</div>
          ${qsHtml}
        </div>
      `;
    })
    .join('');

  const answerKeyHtml = answerRows
    .map((r) => `<li class="dec-ans-row">${escapeHtml(r)}</li>`)
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body {
    font-family: -apple-system, "PingFang TC", "Microsoft JhengHei", "Noto Sans CJK TC", sans-serif;
    color: #000; line-height: 1.5;
  }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 14px; }
  .title { font-size: 22px; font-weight: bold; }
  .headline { font-size: 13px; margin-top: 4px; }
  .meta { font-size: 12px; margin-top: 6px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .scorebar { display: flex; gap: 24px; font-size: 12px; margin-top: 8px; }
  .scorebar .box { border: 1px solid #000; padding: 2px 10px; min-width: 5em; text-align: center; }
  .dec-section { margin-bottom: 16px; }
  .dec-section-head { font-size: 16px; font-weight: bold; border-left: 4px solid #000; padding-left: 8px; margin: 14px 0 6px; }
  .dec-section-pts { font-size: 12px; font-weight: normal; margin-left: 8px; }
  .dec-section-instr { font-size: 12px; color: #333; margin-bottom: 8px; }
  .dec-q { margin-bottom: 14px; page-break-inside: avoid; }
  .dec-prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .dec-line { font-size: 13px; border-bottom: 1px solid #000; min-height: 1.6em; padding: 2px 0; }
  .dec-compare-print { font-size: 13px; }
  .dec-blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .dec-shade-grid { display: flex; border: 1px solid #000; width: max-content; max-width: 100%; margin: 4px 0 8px; }
  .dec-shade-cell { width: 26px; height: 38px; border-right: 1px solid #888; }
  .dec-shade-cell:last-child { border-right: none; }
  .dec-shade-cell.on { background: #b9d2ea; }
  .dec-figure { margin: 4px 0 8px; }
  .dec-figure svg { max-width: 100%; height: auto; }
  .answers { page-break-before: always; }
  .dec-ans-list { list-style: none; padding: 0; margin: 0; columns: 2; font-size: 13px; }
  .dec-ans-row { margin-bottom: 5px; break-inside: avoid; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${title}</div>
  <div class="headline">範圍:${escapeHtml(exam.scope)}</div>
  <div class="meta">
    班級:<span class="blank">&nbsp;</span>
    姓名:<span class="blank">&nbsp;</span>
    日期:<span class="blank">&nbsp;</span>
    卷號:${escapeHtml(exam.id)}
  </div>
  <div class="scorebar">
    <div class="box">作答時間:____ 分</div>
    <div class="box">分數:____ / ${total}</div>
  </div>
</div>
${sectionsHtml}
<div class="answers">
  <div class="dec-section-head">解答(批改用 · 另頁)</div>
  <ul class="dec-ans-list">${answerKeyHtml}</ul>
</div>
</body>
</html>`;
}

/** 小數卷圖示(印表用):塗色格 / 數線 SVG。寬度鎖死,A4 不溢出。 */
function decimalFigurePrintHtml(figure: NonNullable<DecimalQuestion['figure']>): string {
  if (figure.kind === 'shade') {
    const cells = Array.from({ length: figure.total }, (_, i) =>
      `<div class="dec-shade-cell${i < figure.shaded ? ' on' : ''}"></div>`,
    ).join('');
    return `<div class="dec-figure"><div class="dec-shade-grid">${cells}</div></div>`;
  }
  // 數線 SVG(0..maxInt、每大格 10 小格、箭頭指 tenths/10)。
  const W = 460;
  const maxInt = figure.maxInt;
  const totalTicks = maxInt * 10;
  const x0 = 20;
  const x1 = W - 20;
  const span = x1 - x0;
  const px = (t: number): number => x0 + (span * t) / totalTicks;
  let ticks = '';
  for (let t = 0; t <= totalTicks; t++) {
    const big = t % 10 === 0;
    const x = px(t).toFixed(1);
    ticks += `<line x1="${x}" y1="${big ? 26 : 30}" x2="${x}" y2="${big ? 44 : 40}" stroke="#000" stroke-width="${big ? 1.4 : 0.7}"/>`;
    if (big) {
      ticks += `<text x="${x}" y="56" text-anchor="middle" font-size="12" fill="#000">${t / 10}</text>`;
    }
  }
  const ax = px(figure.tenths).toFixed(1);
  const arrow =
    `<line x1="${ax}" y1="10" x2="${ax}" y2="35" stroke="#b03030" stroke-width="1.6"/>` +
    `<path d="M${ax} 10 L${Number(ax) - 5} 2 L${Number(ax) + 5} 2 Z" fill="#b03030"/>`;
  return (
    `<div class="dec-figure">` +
    `<svg width="${W}" height="62" viewBox="0 0 ${W} 62" xmlns="http://www.w3.org/2000/svg">` +
    `<line x1="${x0}" y1="35" x2="${x1}" y2="35" stroke="#000" stroke-width="1.4"/>` +
    `${ticks}${arrow}</svg></div>`
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
